import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import Rating from "@/components/base/Rating";
import SafeHtml from "@/components/base/SafeHtml";
import EmptyState from "@/components/base/EmptyState";
import ApiState from "@/components/base/ApiState";
import BusinessGrid from "@/components/feature/BusinessGrid";
import BusinessReviews from "./components/BusinessReviews";
import EnquiryForm from "./components/EnquiryForm";
import NearbyLocations from "./components/NearbyLocations";
import { getBusinessBySlug, getBusinesses, getCategories, getLocations, getReviews, trackContactEvent } from "@/services/publicApi";
import { useApiResource } from "@/hooks/useApiResource";
import { businessPath, categoryPath } from "@/utils/slug";

export default function BusinessProfile() {
  const { slug = "" } = useParams();
  const { data, loading, error, reload } = useApiResource(
    () => Promise.all([getBusinessBySlug(slug), getBusinesses(), getCategories(), getLocations(), getReviews()]),
    [slug],
  );
  const business = data?.[0] ?? null;
  const all = data?.[1] ?? [];
  const categories = data?.[2] ?? [];
  const locations = data?.[3] ?? [];
  const reviews = data?.[4] ?? [];
  const [copied, setCopied] = useState(false);

  const businessReviews = useMemo(() => reviews.filter((r) => r.businessId === business?.id), [reviews, business]);

  const categorySlug = useMemo(
    () => categories.find((c) => c.id === business?.categoryId)?.slug,
    [categories, business],
  );

  const similar = useMemo(
    () =>
      all
        .filter((b) => b.id !== business?.id && b.categoryId === business?.categoryId)
        .slice(0, 3),
    [all, business],
  );

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!loading && !error && !business) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20">
        <EmptyState
          icon="ri-store-3-line"
          title="Business not found"
          description="This listing may have been removed or the link is incorrect."
          action={{ label: "Browse directory", to: "/directory" }}
        />
      </div>
    );
  }

  const b = business;
  const mapSrc = b ? `https://www.google.com/maps?q=${encodeURIComponent(b.address)}&output=embed` : "";
  const jsonLd = b
    ? {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: b.name,
        description: b.description,
        telephone: b.phone,
        email: b.email,
        address: { "@type": "PostalAddress", streetAddress: b.address, addressLocality: b.locationName, addressRegion: "Karnataka", addressCountry: "IN" },
        ...(b.reviewCount > 0
          ? { aggregateRating: { "@type": "AggregateRating", ratingValue: b.rating, reviewCount: b.reviewCount } }
          : {}),
      }
    : undefined;

  return (
    <>
      <Seo
        title={b ? `${b.name} — ${b.categoryName} in ${b.locationName}` : "Business Profile"}
        description={b ? `${b.description} Contact, hours, reviews and location for ${b.name} in ${b.locationName}.` : undefined}
        keywords={b ? `${b.name}, ${b.categoryName} ${b.locationName}, ${b.tags.join(", ")}` : undefined}
        path={b ? businessPath(b) : `/business/${slug}`}
        image={b?.image}
        jsonLd={jsonLd}
      />

      <div className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-4">
          <Breadcrumbs
            items={[
              { label: "Directory", to: "/directory" },
              { label: b?.categoryName || "…", to: categorySlug ? categoryPath(categorySlug, b?.locationSlug) : "/categories" },
              { label: b?.name || "…" },
            ]}
          />
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-12">
        <ApiState
          loading={loading}
          error={error}
          onRetry={reload}
          skeleton={
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 lg:gap-8">
              <div className="space-y-6">
                <div className="h-72 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
                <div className="h-64 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
              </div>
              <div className="h-72 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
            </div>
          }
        >
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 lg:gap-8">
          <div className="space-y-6">
            <div className="overflow-hidden rounded-lg border border-background-200">
              <div className="relative h-64 md:h-96 w-full">
                {b && (
                  <img src={b.image} alt={`${b.name} in ${b.locationName}`} title={`${b.name} ${b.categoryName}`} className="h-full w-full object-cover object-top" />
                )}
                <div className="absolute inset-x-0 top-0 flex items-center gap-2 p-4">
                  {b?.isFeatured && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-foreground-950">
                      <i className="ri-vip-crown-2-fill"></i> Featured
                    </span>
                  )}
                  {b?.isVerified && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-background-50">
                      <i className="ri-verified-badge-fill"></i> Verified
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-secondary-700">{b?.categoryName}</span>
                  <h1 className="mt-1 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">{b?.name}</h1>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    {b && <Rating value={b.rating} count={b.reviewCount} size="md" />}
                    <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${b?.openNow ? "text-accent-600" : "text-foreground-500"}`}>
                      <i className="ri-time-line"></i> {b?.openNow ? "Open now" : "Closed now"}
                    </span>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-semibold text-secondary-900">
                  <i className="ri-vip-diamond-line"></i> {b?.plan} plan
                </span>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {b?.tags.map((t) => (
                  <span key={t} className="rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-700">{t}</span>
                ))}
              </div>

              <div className="mt-5 border-t border-background-100 pt-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">About</h2>
                <SafeHtml html={b?.description} className="mt-2 text-sm text-foreground-700 leading-relaxed" />
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-background-100 pt-5">
                <div>
                  <h3 className="font-heading text-sm font-bold text-foreground-950">Opening hours</h3>
                  <p className="mt-2 flex items-center gap-2 text-sm text-foreground-700">
                    <i className="ri-time-line text-secondary-500"></i> {b?.hours}
                  </p>
                </div>
                <div>
                  <h3 className="font-heading text-sm font-bold text-foreground-950">Address</h3>
                  <p className="mt-2 flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-map-pin-2-line mt-0.5 text-secondary-500"></i> {b?.address}
                  </p>
                </div>
              </div>
            </div>

            <BusinessReviews businessId={b?.id ?? ""} businessName={b?.name ?? ""} reviews={businessReviews} />

            <EnquiryForm businessId={b?.id ?? ""} businessSlug={b?.slug ?? slug} businessName={b?.name ?? ""} />

            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-extrabold text-foreground-950">Location</h2>
              <p className="mt-1.5 text-sm text-foreground-600">{b?.address}</p>
              <div className="mt-4 overflow-hidden rounded-lg border border-background-200">
                {mapSrc && (
                  <iframe title={`Map to ${b?.name}`} src={mapSrc} className="h-72 w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
                )}
              </div>
            </section>

            <NearbyLocations locations={locations} currentLocationId={b?.locationId} categoryName={b?.categoryName} />
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 h-fit">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">Contact {b?.name}</h2>
              <div className="mt-4 space-y-3">
                <a
                  href={`tel:${(b?.phone || "").replace(/\s/g, "")}`}
                  onClick={() => b && trackContactEvent(b.id, "call")}
                  className="flex items-center gap-3 rounded-md border border-background-200 px-3 py-2.5 text-sm text-foreground-800 hover:border-primary-400 cursor-pointer"
                >
                  <i className="ri-phone-line text-primary-600"></i> {b?.phone}
                </a>
                <a href={`mailto:${b?.email}`} className="flex items-center gap-3 rounded-md border border-background-200 px-3 py-2.5 text-sm text-foreground-800 hover:border-primary-400 cursor-pointer">
                  <i className="ri-mail-line text-primary-600"></i> <span className="truncate">{b?.email}</span>
                </a>
                {b?.website && (
                  <a
                    href={b.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => b && trackContactEvent(b.id, "website")}
                    className="flex items-center gap-3 rounded-md border border-background-200 px-3 py-2.5 text-sm text-foreground-800 hover:border-primary-400 cursor-pointer"
                  >
                    <i className="ri-global-line text-primary-600"></i> <span className="truncate">Visit website</span>
                  </a>
                )}
              </div>
              <div className="mt-4 flex flex-col gap-2">
                <a
                  href={`tel:${(b?.phone || "").replace(/\s/g, "")}`}
                  onClick={() => b && trackContactEvent(b.id, "call")}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line"></i> Call now
                </a>
                <a
                  href={`https://wa.me/${(b?.phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi ${b?.name || ""}, I found you on Mysore Yellow Pages and would like to know more.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => b && trackContactEvent(b.id, "whatsapp")}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm font-bold text-accent-900 hover:bg-accent-100 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i> WhatsApp
                </a>
                <button
                  type="button"
                  onClick={share}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-3 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
                >
                  <i className={copied ? "ri-check-line text-accent-600" : "ri-share-line"}></i>
                  {copied ? "Link copied" : "Share listing"}
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
              <h2 className="inline-flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-customer-service-2-line text-primary-700"></i> Was this helpful?
              </h2>
              <p className="mt-2 text-sm text-foreground-700">
                See a problem with this listing? Let us know so we can keep the directory accurate.
              </p>
              <Link
                to="/contact"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-800 hover:text-primary-900 cursor-pointer"
              >
                Report an issue <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>
        </div>

        {similar.length > 0 && (
          <section className="mt-14">
            <h2 className="font-heading text-lg md:text-xl font-extrabold text-foreground-950">
              Similar businesses in {b?.categoryName}
            </h2>
            <div className="mt-5">
              <BusinessGrid businesses={similar} />
            </div>
          </section>
        )}
        </ApiState>
      </div>
    </>
  );
}